import { ReferralEventData } from '../types/';
import { EventDetailApiResponse, UserMetricsApiResponse } from '../types/api.types';

import { MOCK_REFERRAL_EVENT_DATA } from '../mocks/referral-event.mock';
import { mapParticipationCriteria } from './participation-criteria.helper';

const selectMockOr = <T>(useMockData: boolean, mockValue: T, realValue: T): T => {
  return useMockData ? mockValue : realValue;
};

export const transformApiDataToReferralEventData = (
  apiData: EventDetailApiResponse,
  userMetrics?: UserMetricsApiResponse,
  useMockData = false
): ReferralEventData => {
  // Convert ISO datetime strings to Unix timestamps
  const endTimestamp = Math.floor(new Date(apiData.end_datetime).getTime() / 1000);
  const registrationStartTimestamp = Math.floor(new Date(apiData.registration_start_datetime).getTime() / 1000);
  const registrationEndTimestamp = Math.floor(new Date(apiData.registration_end_datetime).getTime() / 1000);
  const startTimestamp = Math.floor(new Date(apiData.start_datetime).getTime() / 1000);
  const rewardDistributionTimestamp = apiData.reward_distribution_date
    ? Math.floor(new Date(apiData.reward_distribution_date).getTime() / 1000)
    : endTimestamp;

  // Transform participation criteria from API format
  const participationCriteria = mapParticipationCriteria(apiData.participation_criteria);
  const isAma = apiData.event_type === 'ama';
  const isInformational = apiData.event_type === 'informational';

  // Create stages from datetime fields
  const stages = [
    {
      id: 1,
      name: 'REGISTRATION STARTS',
      timestamp: registrationStartTimestamp,
    },
    {
      id: 2,
      name: isAma ? 'AMA STARTS' : isInformational ? 'EVENT STARTS' : 'COMPETITION STARTS',
      timestamp: startTimestamp,
    },
    {
      id: 3,
      name: 'REGISTRATION ENDS',
      timestamp: registrationEndTimestamp,
    },
    {
      id: 4,
      name: isAma ? 'AMA ENDS' : isInformational ? 'EVENT ENDS' : 'COMPETITION ENDS',
      timestamp: endTimestamp,
    },
    {
      id: 5,
      name: 'REWARDS DISTRIBUTION',
      timestamp: rewardDistributionTimestamp,
      // TODO: Uncomment it later, for now timestamp is ok
      // description: apiData.rewards_distributed ? 'Completed' : 'Pending',
    },
  ];

  // Build quickSummaryStats from userMetrics or defaults
  const quickSummaryStats = selectMockOr(useMockData, MOCK_REFERRAL_EVENT_DATA.quickSummaryStats, {
    ctaLabel: 'Connect wallet',
    items: [
      { title: 'Total Referred', value: userMetrics?.total_referred.toString() || '0' },
      { title: 'Total Eligible', value: userMetrics?.total_eligible.toString() || '0' },
      { title: 'Total Purchases', value: userMetrics?.total_purchases.toString() || '0' },
      { title: 'Total RMV Spent', value: userMetrics?.total_rmv_spent?.toFixed(2) || '0' },
      { title: 'Total fNFT Purchases', value: userMetrics?.total_fnft_purchases?.toString() || '0' },
    ],
  });

  // Build chartData from userMetrics time_series_stats
  const chartData = selectMockOr(
    useMockData,
    MOCK_REFERRAL_EVENT_DATA.chartData,
    userMetrics?.time_series_stats
      ? userMetrics.time_series_stats.map((stat) => ({
        label: stat.date,
        value1: stat.total_new_referred,
        value2: stat.total_fnfts,
      }))
      : []
  );

  // Build tableData from userMetrics referred_users_stats
  const emptyTableData = {
    rows: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    },
  };

  const tableData = selectMockOr(
    useMockData,
    MOCK_REFERRAL_EVENT_DATA.tableData,
    userMetrics?.referred_users_stats
      ? {
        rows: userMetrics.referred_users_stats.map((user, index) => ({
          rank: (index + 1).toString(),
          walletAddress: user.wallet_address,
          wnftPurchased: user.metrics.fnft_purchases.toString(),
          prize: user.rewards.map((reward) => reward.name).join(', '),
          isActive: index === 0 && user.rewards.length > 0,
        })),
        pagination: {
          currentPage: 1,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      }
      : emptyTableData
  );

  // Build tiers, currentRewards, nextRewardAt from milestone_progress
  const sortedMilestones = userMetrics?.milestone_progress
    ? [...userMetrics.milestone_progress].sort((a, b) => a.order - b.order)
    : [];

  const tiers = selectMockOr(
    useMockData,
    MOCK_REFERRAL_EVENT_DATA.tiers,
    sortedMilestones.length > 0
      ? sortedMilestones.map((milestone) => ({
        label: milestone.milestone_name,
        requiredRewards: milestone.target,
      }))
      : []
  );

  // Find first incomplete milestone for current progress
  const currentMilestone = sortedMilestones.find((m) => !m.completed) || sortedMilestones[sortedMilestones.length - 1];

  const currentRewards = selectMockOr(
    useMockData,
    MOCK_REFERRAL_EVENT_DATA.currentRewards,
    currentMilestone?.current_value || 0
  );

  const nextRewardAt = selectMockOr(useMockData, MOCK_REFERRAL_EVENT_DATA.nextRewardAt, currentMilestone?.target || 0);

  const nextRewardName = selectMockOr(
    useMockData,
    MOCK_REFERRAL_EVENT_DATA.circularProgressOverlay.nextRewardName,
    currentMilestone?.reward?.name || ''
  );

  const circularProgressOverlay = {
    title: selectMockOr(useMockData, MOCK_REFERRAL_EVENT_DATA.circularProgressOverlay.title, 'Progress'),
    nextRewardName,
  };

  // Build badges from completed milestones
  const badges = selectMockOr(
    useMockData,
    MOCK_REFERRAL_EVENT_DATA.badges,
    sortedMilestones.filter((m) => m.completed).map((m) => ({ text: m.reward.name }))
  );

  // Next milestone name for display purposes
  const nextMilestoneName = selectMockOr(
    useMockData,
    MOCK_REFERRAL_EVENT_DATA.nextMilestoneName,
    currentMilestone?.milestone_name || ''
  );

  // Return transformed data with defaults for missing fields
  return {
    // Data from API
    name: apiData.name,
    tagline: apiData.tagline,
    imageUrl: apiData.image_url,
    bannerUrl: apiData.banner_url,
    iconUrl: apiData.icon_url,

    endAt: endTimestamp,
    stages,
    participationCriteria,
    status: apiData.status,
    isParticipating: apiData.is_participating,

    // QuickSummaryStats from userMetrics or mock
    quickSummaryStats,

    // ChartData and TableData from userMetrics
    chartData,
    tableData,

    // Tiers, progress and rewards from milestone_progress
    tiers,
    currentRewards,
    nextRewardAt,
    nextMilestoneName,
    circularProgressOverlay,
    badges,

    // Analytics events (from mock for now)
    analyticsEvents: selectMockOr(useMockData, MOCK_REFERRAL_EVENT_DATA.analyticsEvents, []),

    eventType: apiData.event_type,
  };
};
